import {
  MachineLearningModal,
  MapBuildingModal,
  MapFetchingModal,
  SpeedLawsModal,
  useUploadFiles,
  VelocitySimulationModal,
} from '@core/core/modules'
import {
  addTabToWorkArea,
  clearAll,
  convertMarkersToPoints,
  copyHorizon,
  deleteElementToTrashForce,
  getProjectById,
  getTabElementProperties,
  getWorkAreasByProjectId,
  initMultipartFiles,
  publishElements,
  selectActiveWorkAreaId,
  selectProjectById,
  setExistingProject,
  setUserInfo,
  startConnectingGeoImportWorks,
  startConnectingML,
  startConnectingProgress,
  startConnectingProjects,
  startConnectingSeismicImportProgress,
  startConnectingTreeObjects,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { ObjectTreeNode } from '@core/core/types'
import { createExportHandler, getProjectDataAnalyticParams } from '@core/core/utils'
import { useProjectMessageSubscription, useSpa } from '@core/hooks'
import { useLocalStorage, useMetaChange } from '@library/hooks'
import type { ModalInterface } from '@library/model'
import { getDisplayName } from '@library/model'
import { parseToken } from '@library/token'
import { booleanToString, stringToBoolean } from '@library/utils'
import { addDeleteElementEventNotification } from '@notifications/actions'
import { Notifications } from '@notifications/Notifications'
import type { Nullable, TokenProps } from 'common/types'
import type { FC } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ApplyVelocityModel } from './ApplyVelocityModel'
import { FileImport, useMultipartUploadFiles } from './FileImport'
import { GeoDbImport } from './GeoDbImport'
import { ProjectHeader } from './Header'
import { MasterImport } from './MasterImport'
import { OverallImport } from './OverallImport'
import css from './Project.css'
import { AddingFilesModalSelector, getActionFromItem, ProjectObjects } from './ProjectObjects'
import { ProjectView } from './ProjectView'
import { Toolbar } from './Toolbar'
import { EChoiceItem, RibbonItemsText } from './types'
import { useMessenger } from './useMessenger'
import { useSignalR } from './useSignalR'

const initialInterface: ModalInterface = {
  closeModal: () => undefined,
  openModal: () => undefined,
  toggleModal: () => undefined,
}

export const Project: FC<TokenProps> = ({ getToken }) => {
  const activeWorkAreaId = useAppSelector(selectActiveWorkAreaId)

  const mlInterfaceRef = useRef<ModalInterface>(initialInterface)
  const mpInterfaceRef = useRef<ModalInterface>(initialInterface)
  const vsInterfaceRef = useRef<ModalInterface>(initialInterface)
  const slInterfaceRef = useRef<ModalInterface>(initialInterface)
  const mapFetchingInterfaceRef = useRef<ModalInterface>(initialInterface)

  const [selectedRibbonItem, setSelectedRibbonItem] = useState<RibbonItemsText | undefined>()
  const [showImportSidebar, setShowImportSidebar] = useState<Nullable<EChoiceItem>>(null)
  const [showObjectsProperties, setShowObjectsProperties] = useState<boolean>(false)

  const [showObjectsTree, setShowObjectsTree] = useLocalStorage(
    'is-object-tree-opened',
    stringToBoolean,
    booleanToString,
    true,
  )
  const { projectId = '' } = useParams()
  const sendSpaEvent = useSpa()
  const dispatch = useAppDispatch()

  const parsedToken = parseToken(getToken?.() ?? '')
  const activeProjectInfo = useAppSelector(state => selectProjectById(state, projectId))

  useProjectMessageSubscription(projectId)

  useEffect(() => {
    if (activeProjectInfo == null) return

    sendSpaEvent(
      'project_open',
      getProjectDataAnalyticParams(activeProjectInfo.name, activeProjectInfo.type),
    )
  }, [activeProjectInfo?.id])

  useLayoutEffect(() => {
    dispatch(getProjectById(projectId))
  }, [projectId])

  useEffect(() => {
    dispatch(getWorkAreasByProjectId(projectId))
    dispatch(startConnectingML())
    dispatch(startConnectingProgress())
    dispatch(startConnectingSeismicImportProgress())
    dispatch(startConnectingGeoImportWorks())
    return () => {
      dispatch(setExistingProject(''))
      dispatch(clearAll())
    }
  }, [projectId])

  useEffect(() => {
    dispatch(getTabElementProperties())
  }, [])

  useEffect(() => {
    dispatch(startConnectingProjects())
    dispatch(startConnectingTreeObjects())
    dispatch(initMultipartFiles(projectId))
    dispatch(setUserInfo(parsedToken))
  }, [])

  useSignalR(projectId)
  useMessenger(projectId)

  const { uploadFiles, uploadMode, objectTreeNodeModal, handleUploadFiles } = useUploadFiles()

  const handleToBin = (elementId: string, elementName: string) => {
    const deleteElementCb = () => {
      dispatch(deleteElementToTrashForce(elementId))
    }

    addDeleteElementEventNotification({
      elementId,
      elementName,
      deleteElementCb,
    })
  }

  const {
    uploadedFiles,
    removeOpenFiles,
    activeUploadedFile,
    updateActiveFile,
    updateFileStep,
    updateFileStatus,
    getFilesFromExplorer,
    updateFileConfig,
    addFilesToProject,
    addCrmsFiles,
    addGeoDFiles,
  } = useMultipartUploadFiles()
  const [objectTreeNode, setObjectTreeNode] = useState<ObjectTreeNode | undefined>()
  const setObjectTreeNodeName = (newObjectTreeNode: ObjectTreeNode) => {
    setObjectTreeNode(prev => ({
      ...prev,
      ...newObjectTreeNode,
    }))
  }

  const handleExportFiles = createExportHandler(projectId)

  const isShowMasterImport =
    activeUploadedFile?.uploadedFile !== undefined &&
    activeUploadedFile.uploadedFile.projectId === projectId
  const showImportPanel =
    !showObjectsTree && showImportSidebar === EChoiceItem.filesImport && !isShowMasterImport
  const isShowProjectView = !isShowMasterImport && !showImportPanel
  const showObjectsPropertiesSidebar = () => {
    setShowObjectsProperties(!showObjectsProperties)
  }
  const showGeoDbImportSidebar = () => {
    setShowImportSidebar(EChoiceItem.geoDBImport)
  }
  const onImportSidebarClose = () => {
    onMasterImportClose()
    setSelectedRibbonItem(undefined)
    setShowImportSidebar(null)
  }
  const onObjectsButtonClick = () => {
    setShowObjectsTree(!showObjectsTree)
    setShowImportSidebar(null)
  }

  const onImportButtonClick = () => {
    onMasterImportClose()
    setShowObjectsTree(false)
    setShowImportSidebar(EChoiceItem.filesImport)
  }

  const handlePublishElements = () => {
    if (objectTreeNode?.uid) {
      dispatch(publishElements(projectId, objectTreeNode.uid))
    }
  }

  const handleCopy = () => {
    if (objectTreeNode?.projectEntityUid) {
      if (objectTreeNode.elementType === 'horizon') {
        dispatch(copyHorizon(projectId, objectTreeNode.projectEntityUid))
      }
    }
  }

  const handleOpenVSPInTable = () => {
    if (!activeWorkAreaId) return

    dispatch(
      addTabToWorkArea(
        activeWorkAreaId,
        'Table',
        getDisplayName('Table'),
        objectTreeNode?.uid,
        projectId,
      ),
    )
  }

  const toggleApplyVelocityModelSidebar = () => {
    showImportSidebar === EChoiceItem.applyVelocityModel
      ? onImportSidebarClose()
      : setShowImportSidebar(EChoiceItem.applyVelocityModel)
  }

  const handleConvertMarkerToPoints = () => {
    const objectTreeNodeName = objectTreeNode?.name
    if (objectTreeNodeName) {
      dispatch(convertMarkersToPoints(objectTreeNodeName, projectId))
    }
  }
  const onMasterImportClose = () => {
    updateActiveFile()
  }
  const onMasterImportOpen = (fileId: string) => {
    updateActiveFile(fileId)
  }

  const onModalClose = () => {
    setSelectedRibbonItem(undefined)
  }
  const getActionFromRibbonItem = (text: RibbonItemsText) => {
    switch (text) {
      case RibbonItemsText.fileUpload: {
        onImportButtonClick()
        return
      }
      case RibbonItemsText.database: {
        showGeoDbImportSidebar()
        return
      }
      case RibbonItemsText.mapping: {
        mpInterfaceRef.current.toggleModal()
        return
      }
      case RibbonItemsText.machineLearning: {
        mlInterfaceRef.current.toggleModal()
        return
      }
      case RibbonItemsText.velocitySimulation: {
        vsInterfaceRef.current.toggleModal()
        return
      }
      case RibbonItemsText.applyVelocityModel: {
        toggleApplyVelocityModelSidebar()
        return
      }
      case RibbonItemsText.speedLaws: {
        slInterfaceRef.current.toggleModal()
        return
      }
      case RibbonItemsText.mapFetching: {
        mapFetchingInterfaceRef.current.toggleModal()
        return
      }
      default:
        break
    }
  }

  useMetaChange(`Геомейт | ${activeProjectInfo?.name}`)

  return (
    <>
      <AddingFilesModalSelector
        objectTreeNodeModal={objectTreeNodeModal}
        uploadFiles={uploadFiles}
        uploadMode={uploadMode}
      />

      <MapBuildingModal ref={mpInterfaceRef} projectId={projectId} onClose={onModalClose} />
      <MachineLearningModal ref={mlInterfaceRef} projectId={projectId} onClose={onModalClose} />
      <VelocitySimulationModal ref={vsInterfaceRef} onClose={onModalClose} />
      <SpeedLawsModal ref={slInterfaceRef} onClose={onModalClose} />
      <MapFetchingModal
        ref={mapFetchingInterfaceRef}
        projectId={projectId}
        onClose={onModalClose}
      />

      <div className={css.header}>
        <ProjectHeader
          getActionFromRibbonItem={getActionFromRibbonItem}
          selectedRibbonItem={selectedRibbonItem}
          setSelectedRibbonItem={setSelectedRibbonItem}
        />
        <div className={css.project}>
          <Toolbar
            showObjectsPropertiesSidebar={showObjectsPropertiesSidebar}
            onObjectsButtonClick={onObjectsButtonClick}
          />
          {!!showObjectsTree && (
            <ProjectObjects
              showObjectsProperties={showObjectsProperties}
              getActionFromItem={item => {
                getActionFromItem(
                  item,
                  mpInterfaceRef.current.toggleModal,
                  showGeoDbImportSidebar,
                  handleUploadFiles,
                  handleConvertMarkerToPoints,
                  handleExportFiles,
                  handleToBin,
                  handleOpenVSPInTable,
                  handlePublishElements,
                  handleCopy,
                  objectTreeNode,
                )
              }}
              showGeoDbImportSidebar={showGeoDbImportSidebar}
              objectTreeNode={objectTreeNode}
              setObjectTreeNode={setObjectTreeNodeName}
            />
          )}

          {showImportSidebar === EChoiceItem.geoDBImport && (
            <GeoDbImport onImportSidebarClose={onImportSidebarClose} />
          )}
          {showImportSidebar === EChoiceItem.filesImport && (
            <FileImport
              uploadedFiles={uploadedFiles}
              removeOpenFiles={removeOpenFiles}
              addFilesToProject={addFilesToProject}
              onImportSidebarClose={onImportSidebarClose}
              onMasterImportOpen={onMasterImportOpen}
            />
          )}
          {showImportSidebar === EChoiceItem.applyVelocityModel && (
            <ApplyVelocityModel onImportSidebarClose={onImportSidebarClose} />
          )}
          {!!isShowProjectView && <ProjectView getToken={getToken} />}
          {!!showImportPanel && (
            <OverallImport
              getFilesFromExplorer={getFilesFromExplorer}
              addCrmsFiles={addCrmsFiles}
              addGeoDFiles={addGeoDFiles}
            />
          )}
          {!!isShowMasterImport && (
            <MasterImport
              file={activeUploadedFile}
              getFilesFromExplorer={getFilesFromExplorer}
              updateFileConfig={updateFileConfig}
              addFilesToProject={addFilesToProject}
              removeOpenFiles={removeOpenFiles}
              onMasterImportClose={onMasterImportClose}
              onStepChanged={updateFileStep}
              onStatusChanged={updateFileStatus}
            />
          )}
          <Notifications />
        </div>
      </div>
    </>
  )
}
